import React, { Fragment } from "react";
import { Tooltip, Collapse, useIcon } from "@SeemplicityDev/shared";
import Highlighter from "react-highlight-words";
import { kebabCase } from "lodash-es";
import ClassNames from "classnames";
import { useFilterBuilderContext } from "./FilterBuilderContext";
import { getFilterFieldDatasource } from "./getFilterFieldDatasource";
import {
  additionalDataCategoryField,
  type FilterFieldCategory,
} from "./groupFieldsByCategory";
import {
  filterKeyEntityClassNames,
  splitFilterKey,
} from "../../FilterManagement/filterKey";

const INITIALLY_EXPANDED_CATEGORY_COUNT = 3;

export interface FilterFieldCategoryListProps {
  categories: FilterFieldCategory[];
  isScope: boolean;
  isSearching: boolean;
  searchValue: string;
  isInitiallyCollapsed?: boolean;
}

const FilterFieldCategoryList: React.FC<FilterFieldCategoryListProps> = ({
  categories,
  isScope,
  isSearching,
  searchValue,
  isInitiallyCollapsed = false,
}) => {
  const { selectFilterField } = useFilterBuilderContext();
  const { getDataSourceIcon } = useIcon();

  const categoryViewKey = isSearching ? "search" : "browse";

  return (
    <>
      {categories.map((category, index) => {
        return (
          <div
            className={ClassNames("filter-category-wrapper", {
              "is-scope": isScope,
            })}
            key={`${categoryViewKey}-category-${category.value}`}
          >
            <Collapse
              label={category.label}
              hasPadding={isScope}
              isLarge
              borderless
              hideBorderBottom={index === categories.length - 1}
              initialState={
                isSearching ||
                isScope ||
                (!isInitiallyCollapsed &&
                  index < INITIALLY_EXPANDED_CATEGORY_COUNT)
              }
            >
              <div
                className={"filter-category"}
                data-testid={`filter-category-${kebabCase(category.label)}`}
              >
                {category.subItems?.map((subItem) => {
                  const datasource =
                    category.field === additionalDataCategoryField
                      ? getFilterFieldDatasource(subItem.related_datasources)
                      : undefined;
                  const datasourceIcon = datasource
                    ? (getDataSourceIcon(datasource, "S", true) as
                        | string
                        | undefined)
                    : undefined;
                  const { entity } = splitFilterKey(subItem.value);

                  return (
                    <Fragment key={`sub-item-${subItem.value}`}>
                      <Tooltip content={subItem.warning} type="info">
                        <span
                          role={"button"}
                          className={ClassNames(
                            "filter-field-selector",
                            { "is-disabled": subItem.disabled },
                            entity && filterKeyEntityClassNames[entity]
                          )}
                          data-testid={`filter-field-${subItem.value}`}
                          onClick={() =>
                            !subItem?.disabled &&
                            selectFilterField(subItem.value)
                          }
                        >
                          {!!datasourceIcon && (
                            <img
                              className={"filter-field-datasource-icon"}
                              src={datasourceIcon}
                              alt={datasource}
                              data-testid={`filter-field-datasource-icon-${subItem.value}`}
                            />
                          )}
                          <Tooltip
                            content={
                              subItem.warning ? undefined : subItem.label
                            }
                            showOnOverflow
                          >
                            <span
                              className={"filter-field-label"}
                              data-testid={`filter-field-label-${subItem.value}`}
                            >
                              <Highlighter
                                highlightClassName="highlight-text-wrap"
                                searchWords={isSearching ? [searchValue] : []}
                                autoEscape
                                textToHighlight={`${subItem.label}`}
                              />
                            </span>
                          </Tooltip>
                        </span>
                      </Tooltip>
                    </Fragment>
                  );
                })}
              </div>
            </Collapse>
          </div>
        );
      })}
    </>
  );
};

export default FilterFieldCategoryList;
